// src/admin/components/CampaignFormDialog.tsx
import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Stack,
  Typography,
} from "@mui/material";
import { uploadCampaignImages } from "../../api/campaignImages";

export type Campaign = {
  id?: string;
  name: string;
  images: string[];
};

type Props = {
  open: boolean;
  initialCampaign: Campaign | null;
  onClose: () => void;
  onSubmit: (values: Campaign) => Promise<void> | void;
};

const MAX_FILES_PER_UPLOAD = 10;

const CampaignFormDialog: React.FC<Props> = ({
  open,
  initialCampaign,
  onClose,
  onSubmit,
}) => {
  const [name, setName] = useState("");
  const [existingImages, setExistingImages] = useState<string[]>([]);
  const [files, setFiles] = useState<File[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // progress (completed files / total)
  const [progress, setProgress] = useState<{
    done: number;
    total: number;
  } | null>(null);

  const [uploadHint, setUploadHint] = useState<string | null>(null);
  const [stuckKey, setStuckKey] = useState<string>("");

  // "stuck" detection
  const STUCK_AFTER_MS = 12000; // 12s
  const stuckTimerRef = React.useRef<number | null>(null);
  const lastProgressRef = React.useRef<string>("");

  useEffect(() => {
    if (open && initialCampaign) {
      setName(initialCampaign.name || "");
      setExistingImages(initialCampaign.images || []);
      setFiles([]);
      setError(null);
      setProgress(null);
      setUploadHint(null);
      lastProgressRef.current = "";
    } else if (open && !initialCampaign) {
      setName("");
      setExistingImages([]);
      setFiles([]);
      setError(null);
      setProgress(null);
      setUploadHint(null);
      lastProgressRef.current = "";
    }
  }, [open, initialCampaign]);

  useEffect(() => {
    const key = saving && progress ? `${progress.done}/${progress.total}` : "";
    setStuckKey(key);
  }, [saving, progress?.done, progress?.total]);

  useEffect(() => {
    // cleanup any existing timer
    if (stuckTimerRef.current) {
      window.clearTimeout(stuckTimerRef.current);
      stuckTimerRef.current = null;
    }

    // Only run while uploading
    if (!saving || !progress) return;

    // If progress changed, clear hint and schedule a new timer
    if (stuckKey && stuckKey !== lastProgressRef.current) {
      lastProgressRef.current = stuckKey;
      setUploadHint(null);
    }

    stuckTimerRef.current = window.setTimeout(() => {
      // If after STUCK_AFTER_MS progress still hasn't moved, show hint
      if (saving && progress && stuckKey === lastProgressRef.current) {
        setUploadHint(
          "If the upload is stuck, your internet might be slow or the images are large. Please wait, or try fewer/smaller images.",
        );
      }
    }, STUCK_AFTER_MS);

    return () => {
      if (stuckTimerRef.current) {
        window.clearTimeout(stuckTimerRef.current);
        stuckTimerRef.current = null;
      }
    };
  }, [saving, progress, stuckKey]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files) return;

    const arr = Array.from(e.target.files);

    if (arr.length > MAX_FILES_PER_UPLOAD) {
      // Keep first 10 (simple, predictable)
      setFiles(arr.slice(0, MAX_FILES_PER_UPLOAD));
      setError(
        `You can upload up to ${MAX_FILES_PER_UPLOAD} images at a time.`,
      );
    } else {
      setFiles(arr);
      setError(null);
    }

    setProgress(null);
    setUploadHint(null);
    lastProgressRef.current = "";

    // allow selecting same files again later
    e.target.value = "";
  };

  const handleSave = async () => {
    if (saving) return;

    if (!name.trim()) {
      setError("Name is required");
      return;
    }

    if (files.length > MAX_FILES_PER_UPLOAD) {
      setError(
        `You can upload up to ${MAX_FILES_PER_UPLOAD} images at a time.`,
      );
      return;
    }

    setSaving(true);
    setError(null);
    setUploadHint(null);
    lastProgressRef.current = "";

    try {
      const uploadedPaths = await uploadCampaignImages(files, (done, total) => {
        setProgress({ done, total });
      });

      const images =
        initialCampaign && initialCampaign.id
          ? [...existingImages, ...uploadedPaths]
          : uploadedPaths;

      await onSubmit({
        id: initialCampaign?.id,
        name: name.trim(),
        images,
      });

      setSaving(false);
      setProgress(null);
      setUploadHint(null);
      onClose();

      onClose();
    } catch (err: any) {
      setSaving(false);
      setProgress(null);
      setUploadHint(null);
      setError(err?.message || "Failed to save campaign");
    }
  };

  const fileLabel =
    files.length === 0
      ? "No files selected"
      : `${files.length} file${files.length > 1 ? "s" : ""} selected`;

  const progressLabel =
    saving && progress
      ? `Uploading images: ${progress.done}/${progress.total}`
      : null;

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle>
        {initialCampaign?.id ? "Edit Campaign" : "New Campaign"}
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} mt={1}>
          <TextField
            label="Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            fullWidth
          />

          <Stack spacing={1}>
            <Typography variant="subtitle2">Images</Typography>
            <Button variant="outlined" component="label" disabled={saving}>
              Select Images from Computer
              <input
                type="file"
                hidden
                multiple
                accept="image/*"
                onChange={handleFileChange}
              />
            </Button>

            <Typography variant="body2" color="text.secondary">
              {fileLabel} (max {MAX_FILES_PER_UPLOAD} at a time)
            </Typography>

            {/* ✅ Progress text (no layout change, just an extra line) */}
            {progressLabel && (
              <Typography variant="body2" color="text.secondary">
                {progressLabel}
              </Typography>
            )}

            {saving && uploadHint && (
              <Typography variant="body2" color="text.secondary">
                {uploadHint}
              </Typography>
            )}

            {existingImages.length > 0 && initialCampaign?.id && (
              <Typography variant="body2" color="text.secondary">
                Existing images: {existingImages.length}
              </Typography>
            )}
          </Stack>

          {error && (
            <Typography color="error" variant="body2">
              {error}
            </Typography>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button onClick={handleSave} variant="contained" disabled={saving}>
          {saving ? "Saving..." : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CampaignFormDialog;
